import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Newspaper, Sparkles } from "lucide-react";
import client, { fmtUsd, fmtPct, fmtNum } from "../lib/api";
import Sparkline from "../components/Sparkline";
import GlobalMarkets from "../components/GlobalMarkets";
import DailyBriefing from "../components/DailyBriefing";
import Correlations from "../components/Correlations";

const StatCard = ({ label, value, delta }) => (
  <div className="glass p-6 flex flex-col gap-1">
    <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
    <div className="font-display text-3xl font-mono-num">{value}</div>
    {delta !== undefined && (
      <div className={`text-sm font-mono-num ${delta >= 0 ? "up" : "down"}`}>{fmtPct(delta)}</div>
    )}
  </div>
);

const Dashboard = () => {
  const [coins, setCoins] = useState([]);
  const [trending, setTrending] = useState([]);
  const [movers, setMovers] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchAll = () => {
      Promise.allSettled([
        client.get("/crypto/markets?per_page=10"),
        client.get("/crypto/trending"),
        client.get("/stocks/movers"),
        client.get("/news"),
      ]).then((res) => {
        if (!mounted) return;
        if (res[0].status === "fulfilled") setCoins(res[0].value.data.results || []);
        if (res[1].status === "fulfilled") setTrending(res[1].value.data.trending || []);
        if (res[2].status === "fulfilled") setMovers(res[2].value.data.results || []);
        if (res[3].status === "fulfilled") setNews((res[3].value.data.articles || []).slice(0, 6));
        setLoading(false);
      });
    };
    fetchAll();
    const id = setInterval(fetchAll, 60_000); // refresh every 60s
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const btc = coins.find((c) => c.id === "bitcoin");
  const eth = coins.find((c) => c.id === "ethereum");

  return (
    <div className="space-y-10 fade-in-up" data-testid="dashboard-page">
      {/* Header row */}
      <div className="flex items-end justify-between">
        <div>
          <span className="chip">Live · Markets</span>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">Markets Overview</h1>
          <p className="text-neutral-400 mt-2 text-sm">Stocks, crypto and AI insights — refreshed every minute.</p>
        </div>
      </div>

      {/* Daily AI Briefing */}
      <DailyBriefing />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="BTC" value={btc ? fmtUsd(btc.current_price, { digits: 0 }) : "—"} delta={btc?.price_change_percentage_24h} />
        <StatCard label="ETH" value={eth ? fmtUsd(eth.current_price, { digits: 0 }) : "—"} delta={eth?.price_change_percentage_24h} />
        <StatCard label="Market Cap" value={btc ? fmtUsd(coins.reduce((a, c) => a + (c.market_cap || 0), 0), { compact: true, digits: 1 }) : "—"} />
        <StatCard label="24h Volume" value={btc ? fmtUsd(coins.reduce((a, c) => a + (c.total_volume || 0), 0), { compact: true, digits: 1 }) : "—"} />
      </div>

      {/* Global Markets */}
      <GlobalMarkets />

      {/* Cross-Asset Correlations */}
      <Correlations />

      {/* Crypto table */}
      <section className="glass p-0 overflow-hidden" data-testid="crypto-table">
        <div className="flex items-center justify-between p-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Top Crypto</div>
            <div className="font-display text-2xl mt-1">Markets by capitalization</div>
          </div>
          <Link to="/markets" data-testid="view-all-crypto" className="text-xs text-neutral-400 hover:text-white">View all →</Link>
        </div>
        <div className="divider" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 text-xs uppercase tracking-wider">
                <th className="px-6 py-3">#</th>
                <th className="px-6 py-3">Asset</th>
                <th className="px-6 py-3 text-right">Price</th>
                <th className="px-6 py-3 text-right">24h</th>
                <th className="px-6 py-3 text-right">7d</th>
                <th className="px-6 py-3 text-right hidden md:table-cell">Market Cap</th>
                <th className="px-6 py-3 hidden lg:table-cell">Last 7d</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-6 py-8 text-neutral-500 text-center">Loading markets…</td></tr>}
              {coins.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors">
                  <td className="px-6 py-4 text-neutral-500 font-mono-num">{c.market_cap_rank}</td>
                  <td className="px-6 py-4">
                    <Link to={`/asset/crypto/${c.id}`} data-testid={`crypto-row-${c.id}`} className="flex items-center gap-3 hover:underline">
                      <img src={c.image} alt={c.name} className="w-7 h-7 rounded-full" />
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-neutral-500 uppercase">{c.symbol}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-right font-mono-num">{fmtUsd(c.current_price)}</td>
                  <td className={`px-6 py-4 text-right font-mono-num ${c.price_change_percentage_24h >= 0 ? "up" : "down"}`}>{fmtPct(c.price_change_percentage_24h)}</td>
                  <td className={`px-6 py-4 text-right font-mono-num ${c.price_change_percentage_7d_in_currency >= 0 ? "up" : "down"}`}>{fmtPct(c.price_change_percentage_7d_in_currency)}</td>
                  <td className="px-6 py-4 text-right font-mono-num hidden md:table-cell">{fmtUsd(c.market_cap, { compact: true, digits: 1 })}</td>
                  <td className="px-6 py-4 hidden lg:table-cell">
                    {c.sparkline_in_7d?.price && <Sparkline data={c.sparkline_in_7d.price} width={120} height={32} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Stocks + Trending + News */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Stock movers */}
        <div className="glass p-6 lg:col-span-5" data-testid="stocks-movers">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Equities</div>
              <div className="font-display text-xl mt-1">Big Tech & Movers</div>
            </div>
          </div>
          <div className="space-y-2">
            {movers.length === 0 && <div className="text-neutral-500 text-sm">Loading or rate-limited by Alpha Vantage…</div>}
            {movers.map((s) => {
              const pct = parseFloat((s.change_percent || "0").replace("%", ""));
              return (
                <Link
                  key={s.symbol}
                  to={`/asset/stock/${s.symbol}`}
                  data-testid={`stock-row-${s.symbol}`}
                  className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/15 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center text-xs font-display">
                      {s.symbol.slice(0, 2)}
                    </div>
                    <div>
                      <div className="font-medium">{s.symbol}</div>
                      <div className="text-xs text-neutral-500">{fmtUsd(s.previous_close)} prev</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-num">{fmtUsd(s.price)}</div>
                    <div className={`text-xs font-mono-num inline-flex items-center gap-1 ${pct >= 0 ? "up" : "down"}`}>
                      {pct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {fmtPct(pct)}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Trending */}
        <div className="glass p-6 lg:col-span-3" data-testid="trending-crypto">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={14} className="text-neutral-400" />
            <div className="text-xs uppercase tracking-widest text-neutral-500">Trending</div>
          </div>
          <div className="space-y-2">
            {trending.slice(0, 7).map((t) => (
              <Link
                key={t.id}
                to={`/asset/crypto/${t.id}`}
                data-testid={`trending-${t.id}`}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5"
              >
                <img src={t.thumb} alt="" className="w-6 h-6 rounded-full" />
                <div className="flex-1 truncate">
                  <div className="text-sm font-medium truncate">{t.name}</div>
                  <div className="text-[10px] uppercase text-neutral-500">{t.symbol}</div>
                </div>
                {t.market_cap_rank && <div className="text-xs text-neutral-500 font-mono-num">#{t.market_cap_rank}</div>}
              </Link>
            ))}
          </div>
        </div>

        {/* News */}
        <div className="glass p-6 lg:col-span-4" data-testid="news-feed">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Newspaper size={14} className="text-neutral-400" />
              <div className="text-xs uppercase tracking-widest text-neutral-500">Latest News</div>
            </div>
            <Link to="/news" className="text-xs text-neutral-400 hover:text-white">All →</Link>
          </div>
          <div className="space-y-4">
            {news.map((a) => (
              <a key={a.url} href={a.url} target="_blank" rel="noreferrer" data-testid={`news-${a.url}`} className="block group">
                <div className="text-sm font-medium leading-snug group-hover:text-blue-400 transition-colors line-clamp-2">{a.title}</div>
                <div className="text-xs text-neutral-500 mt-1 flex gap-2">
                  <span>{a.source}</span>
                  <span>·</span>
                  <span>{a.published_at ? new Date(a.published_at).toLocaleDateString() : ""}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
